import Uppy, { Store as UppyStore } from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';
import { UserS3UploadSignature } from '~/user/types';
import { generateKeyName } from '../utils';
import { UploaderRestrictions } from '../values/UploaderRestrictions';
import { uploadsLogger } from '../loggers';

const uppyLogger = {
  debug: uploadsLogger.extend('uppy').extend('debug'),
  warn: uploadsLogger.extend('uppy').extend('warn'),
  error: uploadsLogger.extend('uppy').extend('error'),
};

export function createUppy({
  id,
  userId,
  s3UploadSignature,
  fileRestrictions,
}: {
  id: string;
  store?: UppyStore;
  userId: string;
  s3UploadSignature: UserS3UploadSignature;
  fileRestrictions: UploaderRestrictions;
}): Uppy {
  const uppyInstance = new Uppy({
    id,
    meta: { uploaderId: id },
    autoProceed: false,
    restrictions: fileRestrictions,
    logger: uppyLogger,
    locale: {
      strings: {
        noDuplicates: "Cannot add '%{fileName}', it already exists",
      },
    },
  });

  const aws3Config = {
    timeout: 60 * 1000,
    limit: 10,
    allowedMetaFields: [
      'ogranizationId',
      'accountId',
      'uploaderId',
      'trackingId',
    ],
    getUploadParameters(file) {
      const url = `https://${s3UploadSignature.bucket}.s3.amazonaws.com`;
      const fields = {
        key: generateKeyName({ file, userId }),
        'Content-Type': file.type || 'application/unknown',
        acl: 'public-read',
        success_action_status: s3UploadSignature.success_action_status,
        policy: s3UploadSignature.base64policy,
        'X-amz-algorithm': s3UploadSignature.algorithm,
        'X-amz-credential': s3UploadSignature.credentials,
        'X-amz-date': s3UploadSignature.date,
        'X-amz-expires': s3UploadSignature.expires,
        'X-amz-signature': s3UploadSignature.signature,
      };

      if (s3UploadSignature.temporarySecurityToken) {
        fields['X-amz-security-token'] =
          s3UploadSignature.temporarySecurityToken;
      }

      return {
        method: 'POST',
        url,
        fields,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      };
    },
  };

  uppyInstance.use(AwsS3, aws3Config);

  return uppyInstance;
}
